'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';

export interface SelectableChipsOption<T extends string> {
  value: T;
  label: string;
}

export interface SelectableChipsProps<T extends string> {
  options: SelectableChipsOption<T>[];
  selected: T[];
  onChange: (next: T[]) => void;
  className?: string;
  disabled?: boolean;
}

export function SelectableChips<T extends string>({
  options,
  selected,
  onChange,
  className,
  disabled = false,
}: SelectableChipsProps<T>) {
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const toggle = (value: T) => {
    if (disabled) return;
    if (selectedSet.has(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)} role="group">
      {options.map((opt) => {
        const isSelected = selectedSet.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            aria-label={opt.label}
            disabled={disabled}
            onClick={() => toggle(opt.value)}
            className={cn(
              'rounded-md border px-3 py-1 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              'disabled:cursor-not-allowed disabled:opacity-50',
              isSelected
                ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                : 'border-input bg-background hover:bg-muted',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
